import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Switch,
  Label,
  Textarea,
  Badge,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { companiesApi } from "@/services/api";
import {
  ArrowLeft,
  Loader2,
  Shield,
  ArrowRightLeft,
  Key,
  Check,
  X,
  Clock,
  User,
  Settings,
} from "lucide-react";
import { formatDate } from "@/lib/utils";

interface Feature {
  featureName: string;
  displayName: string;
  description: string;
  isEnabled: boolean;
  enabledAt?: string;
  enabledByName?: string;
  disabledAt?: string;
  notes?: string;
}

interface CompanyFeatures {
  companyId: string;
  companyName: string;
  features: Feature[];
}

const featureIcons: Record<string, React.ReactNode> = {
  IMPORT_HEDGE: <Shield className="h-5 w-5" />,
  CURRENCY_CONVERSION: <ArrowRightLeft className="h-5 w-5" />,
  EXTERNAL_API: <Key className="h-5 w-5" />,
};

export default function CompanyFeaturesPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [data, setData] = useState<CompanyFeatures | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchFeatures = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const result = await companiesApi.getFeatures(id);
      setData(result);
    } catch (err) {
      setError("Erro ao carregar features da empresa");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, [id]);

  // Limpar mensagem de sucesso após 3 segundos
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleToggleFeature = async (featureName: string, currentEnabled: boolean) => {
    if (!id) return;

    const newEnabled = !currentEnabled;
    const note = notes[featureName]?.trim();

    try {
      setSaving(featureName);
      setError(null);
      await companiesApi.updateFeature(id, featureName, {
        isEnabled: newEnabled,
        notes: note || undefined,
      });

      // Clear notes after successful save
      setNotes((prev) => ({ ...prev, [featureName]: "" }));

      // Refresh data
      await fetchFeatures();

      setSuccessMessage(
        newEnabled
          ? `Feature "${featureName}" habilitada com sucesso`
          : `Feature "${featureName}" desabilitada com sucesso`
      );
    } catch (err) {
      setError("Erro ao atualizar feature");
      console.error(err);
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Empresa não encontrada</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/admin/companies")}>
          Voltar para empresas
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Settings className="h-5 w-5 sm:h-6 sm:w-6" />}
        title={`Features - ${data.companyName}`}
        description="Gerencie quais funcionalidades estão habilitadas para esta empresa"
        actions={
          <Button className="bg-white/20 hover:bg-white/30 text-white border-0" onClick={() => navigate("/admin/companies")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        }
      />

      {/* Mensagens de feedback */}
      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg flex items-center gap-2">
          <X className="h-5 w-5" />
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 text-green-800 p-4 rounded-lg flex items-center gap-2">
          <Check className="h-5 w-5" />
          {successMessage}
        </div>
      )}

      <div className="grid gap-4">
        {data.features.map((feature) => (
          <Card key={feature.featureName}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg ${
                      feature.isEnabled
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {featureIcons[feature.featureName] || <Shield className="h-5 w-5" />}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{feature.displayName}</CardTitle>
                    <CardDescription>{feature.description}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Badge variant={feature.isEnabled ? "success" : "secondary"}>
                    {feature.isEnabled ? (
                      <>
                        <Check className="h-3 w-3 mr-1" />
                        Habilitado
                      </>
                    ) : (
                      <>
                        <X className="h-3 w-3 mr-1" />
                        Desabilitado
                      </>
                    )}
                  </Badge>
                  <Switch
                    checked={feature.isEnabled}
                    onCheckedChange={() =>
                      handleToggleFeature(feature.featureName, feature.isEnabled)
                    }
                    disabled={saving === feature.featureName}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid gap-4">
                {/* Histórico */}
                {(feature.enabledAt || feature.disabledAt) && (
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {feature.isEnabled && feature.enabledAt && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>Habilitado em {formatDate(feature.enabledAt)}</span>
                        {feature.enabledByName && (
                          <>
                            <User className="h-4 w-4 ml-2" />
                            <span>por {feature.enabledByName}</span>
                          </>
                        )}
                      </div>
                    )}
                    {!feature.isEnabled && feature.disabledAt && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>Desabilitado em {formatDate(feature.disabledAt)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Observações anteriores */}
                {feature.notes && (
                  <div className="text-sm bg-muted/50 p-3 rounded-lg">
                    <span className="font-medium">Observação:</span> {feature.notes}
                  </div>
                )}

                {/* Campo de observação para mudança */}
                <div className="space-y-2">
                  <Label htmlFor={`notes-${feature.featureName}`} className="text-sm">
                    Observação (opcional)
                  </Label>
                  <div className="flex gap-2">
                    <Textarea
                      id={`notes-${feature.featureName}`}
                      placeholder="Motivo da alteração..."
                      value={notes[feature.featureName] || ""}
                      onChange={(e) =>
                        setNotes((prev) => ({ ...prev, [feature.featureName]: e.target.value }))
                      }
                      className="h-20 text-sm"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
